import { QRCodeSVG } from "qrcode.react";
import { useState } from "react";

import { useUI } from "../store";

// Extracted from the login prototype's floating mascot section. Everything else
// in that prototype (card, form, TH/EN switch, logos) already existed here as
// working components, so only this widget is new.
//
// Two deliberate departures from the prototype source:
//
// 1. Colours are the themed tokens, not literals. `bg-pane-left` resolves to
//    #171E27, `accent` to #55C9B3, `border-line` to #2A3644 -- byte-identical in
//    dark mode (src/theme.test.ts pins that), but the widget also follows the
//    light/dark toggle instead of staying dark forever.
// 2. `animate-bubble-pop` is the prototype's `chat-bubble-fade` inverted so it
//    survives `prefers-reduced-motion` -- see the note in tailwind.config.ts.
//
// The shadows keep the prototype's literal rgba(): a drop shadow and a mint glow
// are not in the palette, and there is nothing sensible to map them onto.
//
// The QR and mascot are fixed together at the viewport corner. This keeps the
// QR visible without consuming any space inside the sign-in card.

/** The site a participant opens on their phone. Also the QR payload. */
const SITE_URL = "https://dnsws.in.th";

const text = (language: string) =>
  language === "th"
    ? {
        label: "คำแนะนำสำหรับการเข้าสู่ระบบ",
        heading: "ต้องการรหัสผ่านใช่ไหมครับ?",
        scan: <>สแกน QR Code นี้<br />เพื่อเข้าสู่เว็บไซต์ <strong className="text-accent">dnsws.in.th</strong> จากมือถือได้เลยครับ!</>,
        tipsLabel: "Tips:",
        tip: <>ขอรับ <strong className="text-ink-strong">ชื่อผู้ใช้</strong> และ <strong className="text-ink-strong">รหัสผ่าน</strong> ของกลุ่มท่าน ได้จากผู้ช่วยวิทยากร(TA) ก่อนเริ่มทำ Lab นะครับ</>,
        qrAlt: "QR Code สำหรับเปิดเว็บไซต์ dnsws.in.th",
        openHelp: "เปิดคำแนะนำการเข้าสู่ระบบ",
        closeHelp: "ปิดคำแนะนำการเข้าสู่ระบบ",
      }
    : {
        label: "Sign-in help",
        heading: "Need your credentials?",
        scan: <>Scan this QR code<br />to open <strong className="text-accent">dnsws.in.th</strong> on your phone.</>,
        tipsLabel: "Tips:",
        tip: <>Ask a teaching assistant (TA) for your group's <strong className="text-ink-strong">username</strong> and <strong className="text-ink-strong">password</strong> before starting the lab.</>,
        qrAlt: "QR code linking to dnsws.in.th",
        openHelp: "Open sign-in help",
        closeHelp: "Close sign-in help",
      };

export function MascotWidget() {
  const { language } = useUI();
  const t = text(language);
  const [open, setOpen] = useState(true);
  return (
    <>
      <aside
        id="mascot-sign-in-help"
        aria-label={t.label}
        className="animate-bubble-pop fixed bottom-[4.75rem] right-4 z-40 grid w-[min(17.5rem,calc(100vw-2rem))] grid-cols-[64px_minmax(0,1fr)] items-center gap-2.5 rounded-xl border-2 border-accent bg-pane-left p-2.5 shadow-[0_18px_48px_rgba(0,0,0,0.5)] sm:bottom-[5.5rem] sm:right-6 min-[860px]:bottom-28 min-[860px]:right-8 min-[860px]:grid-cols-[68px_minmax(0,1fr)]"
      >
        {/* Generated locally, with the four-module quiet zone embedded in the
            SVG so it remains scannable in both themes and at every breakpoint. */}
        <div className="size-16 rounded-lg bg-white p-0.5 min-[860px]:size-[68px]">
          <QRCodeSVG value={SITE_URL} role="img" aria-label={t.qrAlt} marginSize={4} className="size-full" />
        </div>
        <div className="min-w-0">
          <h2 className="m-0 text-[0.72rem] font-semibold leading-4 text-ink-strong">{t.heading}</h2>
          <p className="mb-0 mt-1 text-[0.64rem] leading-4 text-ink-muted">{t.scan}</p>
        </div>
        {open && <div className="col-span-2 mt-0.5 border-t border-line pt-2">
          <p className="mb-0 mt-1 text-[0.66rem] leading-4 text-ink"><span className="mr-1 font-bold text-accent">💡 {t.tipsLabel}</span>{t.tip}</p>
        </div>}
      </aside>

      {/* The mascot floats independently at the viewport corner and toggles a
          detailed tip. The QR card above remains visible in either state. */}
      <button
        type="button"
        aria-label={open ? t.closeHelp : t.openHelp}
        aria-expanded={open}
        aria-controls="mascot-sign-in-help"
        onClick={() => setOpen((value) => !value)}
        className="animate-float fixed bottom-4 right-4 z-50 flex size-12 items-center justify-center rounded-full border-2 border-accent bg-gradient-to-tr from-pane-right to-line text-accent shadow-[0_0_15px_rgba(85,201,179,0.3)] transition-colors hover:bg-accent/10 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent sm:bottom-6 sm:right-6 sm:size-14 min-[860px]:bottom-8 min-[860px]:right-8 min-[860px]:size-16"
      >
        <svg className="size-6 sm:size-7 min-[860px]:size-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          <circle cx="9" cy="9" r="1" fill="currentColor" />
          <circle cx="15" cy="9" r="1" fill="currentColor" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 13h6" />
        </svg>
      </button>
    </>
  );
}
